import { ReactNode } from "react";
import { useTranslations } from "use-intl";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BankAccountsStatus, TransactionSuggestion } from "@/types/payments";
import BankStatusUnknownState from "@/components/shared/BankStatusUnknownState";
import StillCheckingState from "@/components/shared/StillCheckingState";
import LoadFailedState from "@/components/shared/LoadFailedState";
import CandidateMatchTable from "@/components/shared/CandidateMatchTable";
import UnmatchedAcknowledgment from "@/components/shared/UnmatchedAcknowledgment";

interface MatchModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  /** The vendor/customer/description line shown under the dialog title. */
  subtitle: ReactNode;
  /** #498 Task 8 — the "editing an existing payment record" banner; null whenever this is a
   * first-time mark-as-X (not yet terminal status). Payables has no correction banner. */
  correctionBanner?: ReactNode;

  hasBankAccounts: BankAccountsStatus;
  showBankStatusUnknown: boolean;
  showStillChecking: boolean;
  showLoadFailed: boolean;
  showCandidateTable: boolean;
  onRetry: () => void;

  /** Case a (hasBankAccounts === false) — the trusted date-picker-only content; differs per
   * caller (ExternalSettlementFields for payables/receivables, a bare Calendar for receipts). */
  caseAContent: ReactNode;
  onConfirmCaseA: () => void;
  caseAConfirmLabel: string;
  caseAConfirmDisabled: boolean;

  /** Case b — candidate transactions found. */
  suggestions: TransactionSuggestion[];
  selectedSuggestionId: string | null;
  onSelectSuggestion: (suggestion: TransactionSuggestion) => void;
  formatCurrency: (amount: number, currency: string) => string;
  onSearchAgain: () => void;
  onNoneOfTheseMatch: () => void;
  onConfirmMatch: () => void;
  confirmMatchDisabled: boolean;
  /** Payables only: shows "Confirming..." while the async confirm RPC is in flight. */
  confirmMatchBusy?: boolean;

  /** Case c — zero candidates, or "None of these match" was clicked. */
  acknowledgeCheckboxId: string;
  acknowledged: boolean;
  onAcknowledgedChange: (checked: boolean) => void;
  /** The settlement-fields content shown under the acknowledgment checkbox — differs per caller
   * (receivables never shows the SEK field here; payables and receipts do). */
  caseCContent: ReactNode;
  noneOfTheseMatch: boolean;
  onBackToSuggestions: () => void;
  onConfirmAcknowledgment: () => void;
  confirmAcknowledgmentDisabled: boolean;
}

/**
 * The entire "match to transaction" dialog (issue #498), shared verbatim across payables'
 * PayableMatchSuggestionsModal, receivables' MarkAsReceivedModal and receipts' MarkAsMatchedModal
 * — the Dialog shell and the five view states (bank-status-unknown, case a/no accounts,
 * still-checking, load-failed, candidates-found, zero-candidates/acknowledgment) are identical
 * shape across all three document types; only the subtitle, an optional correction banner, and
 * case a's/case c's inner settlement-fields content differ per caller.
 */
export default function MatchModalBody({
  open,
  setOpen,
  title,
  subtitle,
  correctionBanner,
  hasBankAccounts,
  showBankStatusUnknown,
  showStillChecking,
  showLoadFailed,
  showCandidateTable,
  onRetry,
  caseAContent,
  onConfirmCaseA,
  caseAConfirmLabel,
  caseAConfirmDisabled,
  suggestions,
  selectedSuggestionId,
  onSelectSuggestion,
  formatCurrency,
  onSearchAgain,
  onNoneOfTheseMatch,
  onConfirmMatch,
  confirmMatchDisabled,
  confirmMatchBusy,
  acknowledgeCheckboxId,
  acknowledged,
  onAcknowledgedChange,
  caseCContent,
  noneOfTheseMatch,
  onBackToSuggestions,
  onConfirmAcknowledgment,
  confirmAcknowledgmentDisabled,
}: MatchModalProps) {
  const t = useTranslations("consultantMatching.shared");
  const onCancel = () => setOpen(false);

  const body = (() => {
    if (showBankStatusUnknown) {
      return (
        <BankStatusUnknownState
          hasBankAccounts={hasBankAccounts as "loading" | "error"}
          onCancel={onCancel}
        />
      );
    }
    if (hasBankAccounts === false) {
      return (
        <div className="space-y-4">
          {caseAContent}
          <DialogFooter>
            <Button variant="outline" onClick={onCancel}>{t("cancel")}</Button>
            <Button onClick={onConfirmCaseA} disabled={caseAConfirmDisabled}>
              {caseAConfirmLabel}
            </Button>
          </DialogFooter>
        </div>
      );
    }
    if (showStillChecking) {
      return <StillCheckingState />;
    }
    if (showLoadFailed) {
      return <LoadFailedState onCancel={onCancel} onRetry={onRetry} />;
    }
    if (showCandidateTable) {
      return (
        <CandidateMatchTable
          suggestions={suggestions}
          selectedId={selectedSuggestionId}
          onSelect={onSelectSuggestion}
          formatCurrency={formatCurrency}
          onSearchAgain={onSearchAgain}
          onNoneOfTheseMatch={onNoneOfTheseMatch}
          onCancel={onCancel}
          onConfirm={onConfirmMatch}
          confirmDisabled={confirmMatchDisabled}
          confirmBusy={confirmMatchBusy}
        />
      );
    }
    return (
      <UnmatchedAcknowledgment
        checkboxId={acknowledgeCheckboxId}
        acknowledged={acknowledged}
        onAcknowledgedChange={onAcknowledgedChange}
        noneOfTheseMatch={noneOfTheseMatch}
        onBackToSuggestions={onBackToSuggestions}
        onCancel={onCancel}
        onConfirm={onConfirmAcknowledgment}
        confirmDisabled={confirmAcknowledgmentDisabled}
      >
        {caseCContent}
      </UnmatchedAcknowledgment>
    );
  })();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="py-2 text-sm text-muted-foreground">{subtitle}</div>

        {correctionBanner}

        {body}
      </DialogContent>
    </Dialog>
  );
}
